import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronRight, Loader2, Building2, Users, X, SearchX } from "lucide-react";
import { useListControls, ListToolbar, ListPagination } from "@/components/ListControls";

const scoreColor = (s) => (s >= 70 ? "text-[#00A859]" : s >= 40 ? "text-[#F59E0B]" : "text-destructive");
const empty = { name: "", sector: "", notes: "", contacts: [] };

export default function ClientsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const navigate = useNavigate();
  const { canEdit } = useAuth();

  const controls = useListControls(items, {
    searchFields: ["name", "sector"],
    filters: {
      sector: { label: "Settore", get: (c) => c.sector },
      tier: { label: "Maturità", get: (c) => c.latest_tier?.label },
    },
    sortOptions: [
      { value: "score_desc", label: "Punteggio ↓", cmp: (a, b) => (b.latest_score ?? -1) - (a.latest_score ?? -1) },
      { value: "name", label: "Nome A–Z", cmp: (a, b) => (a.name || "").localeCompare(b.name || "") },
      { value: "assessments", label: "N. assessment ↓", cmp: (a, b) => (b.assessments_count || 0) - (a.assessments_count || 0) },
    ],
    pageSize: 9,
  });

  const load = async () => { const { data } = await api.get("/clients"); setItems(data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c, e) => { e.stopPropagation(); setForm({ name: c.name, sector: c.sector || "", notes: c.notes || "", contacts: c.contacts || [] }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nome cliente obbligatorio"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/clients/${editId}`, form);
      else await api.post("/clients", form);
      toast.success("Cliente salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare il cliente? Gli assessment collegati resteranno ma verranno scollegati.")) return;
    await api.delete(`/clients/${id}`); toast.success("Cliente eliminato"); load();
  };

  const addContact = () => setForm((f) => ({ ...f, contacts: [...f.contacts, { name: "", role: "", email: "", phone: "" }] }));
  const updContact = (i, k, v) => setForm((f) => ({ ...f, contacts: f.contacts.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const rmContact = (i) => setForm((f) => ({ ...f, contacts: f.contacts.filter((_, idx) => idx !== i) }));

  return (
    <Layout>
      <PageHeader title="Clienti" subtitle={`${items.length} clienti in portfolio`}>
        {canEdit && <Button data-testid="add-client-btn" onClick={openNew} className="rounded-none"><Plus className="w-4 h-4 mr-2" /> Nuovo cliente</Button>}
      </PageHeader>

      <div className="p-8">
        {loading ? <div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> : items.length === 0 ? (
          <div className="border border-dashed border-border bg-white p-16 text-center">
            <Building2 className="w-10 h-10 text-muted-foreground mx-auto" />
            <p className="text-muted-foreground mt-4">Nessun cliente. Crea un cliente per raggruppare i suoi assessment nel tempo.</p>
            {canEdit && <Button onClick={openNew} className="mt-4 rounded-none"><Plus className="w-4 h-4 mr-2" /> Crea il primo cliente</Button>}
          </div>
        ) : (
          <>
            <ListToolbar controls={controls} searchPlaceholder="Cerca cliente, settore…" />
            {controls.filtered.length === 0 ? (
              <div className="border border-dashed border-border bg-white p-16 text-center" data-testid="clients-no-results">
                <SearchX className="w-10 h-10 text-muted-foreground mx-auto" />
                <p className="text-muted-foreground mt-4">Nessun cliente corrisponde ai filtri.</p>
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-list">
            {controls.visible.map((c, i) => (
              <div key={c.id} data-testid={`client-card-${i}`} onClick={() => navigate(`/clients/${c.id}`)} className="bg-white border border-border p-5 cursor-pointer hover:bg-secondary transition-colors duration-150">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-heading font-semibold tracking-tight">{c.name}</h3>
                    <div className="data-label mt-1">{c.sector || "—"}</div>
                  </div>
                  {c.latest_score != null && (
                    <div className="text-right">
                      <div className={`font-mono font-bold text-2xl ${scoreColor(c.latest_score)}`}>{c.latest_score}</div>
                      {c.latest_tier && <span className="text-[10px] font-bold text-white px-1.5 py-0.5" style={{ backgroundColor: c.latest_tier.color }}>{c.latest_tier.label}</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                  <span>{c.assessments_count} assessment</span>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{(c.contacts || []).length}</span>
                </div>
                {(c.trend || []).length >= 2 && (
                  <ResponsiveContainer width="100%" height={40}>
                    <LineChart data={c.trend.map((t) => ({ v: t.overall }))} margin={{ top: 6, bottom: 0, left: 0, right: 0 }}>
                      <Line type="monotone" dataKey="v" stroke="#002FA7" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                  <div className="flex gap-3">
                    {canEdit && <button onClick={(e) => openEdit(c, e)} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>}
                    {canEdit && <button onClick={(e) => remove(c.id, e)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
            )}
            <ListPagination controls={controls} />
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">{editId ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Nome *</Label><Input data-testid="client-name-input" className="mt-1 rounded-none" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label className="data-label">Settore</Label><Input className="mt-1 rounded-none" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} /></div>
            </div>
            <div><Label className="data-label">Note</Label><Textarea className="mt-1 rounded-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="data-label">Referenti</Label>
                <Button type="button" size="sm" variant="outline" className="rounded-none h-7" onClick={addContact} data-testid="add-contact-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi</Button>
              </div>
              <div className="space-y-2">
                {form.contacts.map((c, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-4 rounded-none h-8 text-sm" placeholder="Nome" value={c.name} onChange={(e) => updContact(i, "name", e.target.value)} />
                    <Input className="col-span-3 rounded-none h-8 text-sm" placeholder="Ruolo" value={c.role} onChange={(e) => updContact(i, "role", e.target.value)} />
                    <Input className="col-span-4 rounded-none h-8 text-sm" placeholder="Email" value={c.email} onChange={(e) => updContact(i, "email", e.target.value)} />
                    <button type="button" onClick={() => rmContact(i)} className="col-span-1 text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                {form.contacts.length === 0 && <p className="text-xs text-muted-foreground">Nessun referente.</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-client-btn" className="rounded-none" onClick={save} disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
